import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  deleteV1CompaniesCompanyIdMaterialsId,
  MatchKeyMatchableType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CSVImportExportButton } from '~components/CSVImportExport/CSVImportExportButton';
import { CSVImportExportModal } from '~components/CSVImportExport/CSVImportExportModal';
import { useCSVModal } from '~components/CSVImportExport/useCSVModal';
import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { matchKeysColumn } from '~components/MatchKeys/matchKeysColumn';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { FeatureFlags } from '~constants/featureFlags';
import useEntityCsvConfig from '~hooks/useCSVImportExport/useEntityCsvConfig';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import { Material } from '~hooks/useMaterials/models';
import useMaterials from '~hooks/useMaterials/useMaterials';
import { useUserRoles } from '~hooks/useUsers/useUserRoles';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import useCompanyMatchKeysQuery from '~queries/useCompanyMatchKeysQuery';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { dateFormat } from '~utils/dateTime';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import MaterialDialog from './MaterialDialog';

type MaterialDialogState = {
  isOpen: boolean;
  material?: Material;
};

const MaterialsManagement = observer(() => {
  const [materialFormDialogState, setMaterialFormDialogState] =
    useState<MaterialDialogState>({
      isOpen: false,
    });

  const [deleteMaterialDialogState, setDeleteMaterialDialogState] = useState<
    MaterialDialogState & { isSubmitting: boolean }
  >({ isOpen: false, isSubmitting: false });

  const { companyAssetsStore, userStore, toasterStore } = useStores();
  const { getUserRoles } = useUserRoles();
  const { getMaterialsByCompanyId } = useMaterials();
  const { searchValue, setSearchQueryValue } = useDataGridSearch();
  const companyId = getEffectiveUserCompanyId(userStore);
  const matchKeysEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.matchKeysEnabled,
  });
  const showMatchKeys = matchKeysEnabled && userStore.getPermissions().canViewMatchKey;
  const { matchKeysByMatchableId } = useCompanyMatchKeysQuery({
    companyId,
    isEnabled: showMatchKeys,
    matchableType: MatchKeyMatchableType.MATERIAL,
  });

  const {
    goToPage: goToMaterialsPage,
    isFetching: isFetchingMaterials,
    pagination: materialsPagination,
    reloadCurrentPage: reloadMaterialsPage,
    rows,
    startOver: startMaterialsOver,
  } = useCursorPagination<Material>({
    fetchPage: ({ cursor, limit, link, signal }) =>
      getMaterialsByCompanyId({
        companyId,
        cursor,
        limit,
        link,
        searchQuery: searchValue,
        signal,
      }),
    limit: MAX_API_PAGE_SIZE,
  });

  const { config: materialsConfig, loadConfig: loadMaterialsConfig } = useEntityCsvConfig(
    'materials',
    companyId || '',
  );

  const goToFirstMaterialsPage = useCallback(() => {
    void goToMaterialsPage();
  }, [goToMaterialsPage]);

  const csvModal = useCSVModal({ onImportComplete: goToFirstMaterialsPage });

  useEffect(() => {
    getUserRoles();
  }, [companyId]);

  useEffect(() => {
    void startMaterialsOver();
  }, [companyId, searchValue]);

  const onChangeQuickFilter = (searchQuery: string) => {
    setSearchQueryValue(searchQuery);
  };

  const handleCreateNewMaterialClick = useCallback(() => {
    setMaterialFormDialogState({ isOpen: true });
  }, []);

  const handleDeleteMaterialClick = useCallback((material: Material) => {
    setDeleteMaterialDialogState({ isOpen: true, isSubmitting: false, material });
  }, []);

  const handleCloseMaterialFormDialog = useCallback(() => {
    setMaterialFormDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleCloseDeleteMaterialDialog = useCallback(() => {
    setDeleteMaterialDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const deleteMaterial = useCallback(async () => {
    const materialId = deleteMaterialDialogState.material?.id;

    if (!materialId) {
      return;
    }

    try {
      setDeleteMaterialDialogState((state) => ({ ...state, isSubmitting: true }));

      await deleteV1CompaniesCompanyIdMaterialsId({
        path: {
          'company-id': companyId,
          id: materialId,
        },
      });

      companyAssetsStore.deleteMaterial(materialId);
      void reloadMaterialsPage();
      toasterStore.push(
        alert(t('administration.material.material_deleted'), AlertTypes.success),
      );
      setDeleteMaterialDialogState({ isOpen: false, isSubmitting: false });
    } catch (error) {
      setDeleteMaterialDialogState((state) => ({ ...state, isSubmitting: false }));
      connection.handleRequestError(
        error,
        t('error_messages.materials.failed_to_delete'),
      );
    }
  }, [
    deleteMaterialDialogState.material?.id,
    reloadMaterialsPage,
    companyAssetsStore,
    companyId,
  ]);

  const handleMaterialSaved = useCallback(() => {
    const isEditingMaterial = Boolean(materialFormDialogState.material);

    if (isEditingMaterial) {
      void reloadMaterialsPage();
    } else {
      void goToMaterialsPage();
    }
  }, [materialFormDialogState.material, reloadMaterialsPage, goToMaterialsPage]);

  const columns = useMemo<Array<DataTableColumn<Material>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        valueGetter: (material) => material.name,
      },
      {
        field: 'typeName',
        headerName: t('form_fields.material_type'),
        renderCell: (material) => (
          <Tooltip title={`${t('form_fields.material_type')}: ${material.typeName}`}>
            <span>{material.typeName}</span>
          </Tooltip>
        ),
      },
      {
        field: 'companyName',
        headerName: t('form_fields.company'),
        valueGetter: (material) => material.companyName,
      },
      {
        field: 'externalId',
        headerName: t('form_fields.material_id'),
        valueGetter: (material) => material.externalId,
      },
      {
        field: 'isActive',
        headerName: t('form_fields.status'),
        renderCell: (material) => (
          <Chip
            label={material.isActive ? t('common.active') : t('common.inactive')}
            color={material.isActive ? 'success' : 'default'}
            variant="outlined"
          />
        ),
      },
      ...(showMatchKeys ? [matchKeysColumn<Material>(matchKeysByMatchableId)] : []),
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        valueGetter: (material) => dateFormat(material.createdAt),
      },
      actionsColumn((material) => {
        const options = [
          {
            title: t('actions.delete'),
            icon: <Delete />,
            callBack: () => handleDeleteMaterialClick(material),
          },
        ];

        return (
          <>
            <IconButton
              size="small"
              onClick={() => setMaterialFormDialogState({ isOpen: true, material })}
            >
              <Edit />
            </IconButton>
            <SimpleMenu options={options}>
              <MoreHoriz />
            </SimpleMenu>
          </>
        );
      }),
    ],
    [handleDeleteMaterialClick, matchKeysByMatchableId, showMatchKeys],
  );

  return (
    <>
      <SettingsPageHeader
        title={t('administration.material.title')}
        description={t('settings.page_descriptions.materials')}
      />
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 2 }}>
        <CSVImportExportButton onClick={csvModal.open} />
        <SmallButton
          color="brandV2Yellow"
          onClick={handleCreateNewMaterialClick}
          startIcon={<Add />}
        >
          {t('administration.material.create_material')}
        </SmallButton>
      </Box>
      <Box data-test-id={'materials-data-grid'}>
        <DataTable
          id="material-data-grid-view"
          columns={columns}
          rows={rows}
          loading={isFetchingMaterials || deleteMaterialDialogState.isSubmitting}
          onSearchChange={onChangeQuickFilter}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isFetchingMaterials || deleteMaterialDialogState.isSubmitting}
              onNavigate={(link) => void goToMaterialsPage(link)}
              pagination={materialsPagination}
            />
          }
        />
      </Box>
      <MaterialDialog
        isOpen={materialFormDialogState.isOpen}
        material={materialFormDialogState.material}
        onClose={handleCloseMaterialFormDialog}
        onSuccess={handleMaterialSaved}
      />
      <ConfirmationDialog
        isBusy={deleteMaterialDialogState.isSubmitting}
        isOpen={deleteMaterialDialogState.isOpen}
        onClose={handleCloseDeleteMaterialDialog}
        onConfirm={deleteMaterial}
        title={t('administration.material.delete_material_title')}
      >
        <Typography>{t('administration.material.delete_material_text')}</Typography>
      </ConfirmationDialog>
      <CSVImportExportModal
        isOpen={csvModal.isOpen}
        onClose={csvModal.close}
        config={materialsConfig}
        loadConfig={loadMaterialsConfig}
        companyId={companyId || ''}
        entityDisplayName={t('form_fields.materials')}
        onImportComplete={csvModal.handleImportComplete}
      />
    </>
  );
});

export { MaterialsManagement };
